import React, { useMemo, useState } from 'react';
import { fmt } from '../kuroco/fmt';
import { FOCUS_SLOTS, getFocusDef } from '../../utils/kpiFocusCatalog';
import { KPI_BY_FOCUS_KEY, readKpiValue } from '../../utils/kpiRegistry';

/**
 * スタイリスト一覧 — columns = stylists of one store (real names from the DB), rows = metrics.
 * Sticky metric-label column + stylist header row, vertical scroll, tap a row label to sort columns.
 * Every value comes from GET /dashboard/admin/roster (joined server-side); a missing value
 * shows 「—」, never a placeholder.
 *
 * 色は、サーバーが付ける status（good / mid / warn / none）をそのまま使う。閾値（SALES_PCT /
 * ACTION_RATE）はバックエンドの 1 か所（sales_status）にあり、フロントは持たない。status が無い
 * （古い応答・サーバーが判定できなかった）ときは、色を付けない。
 */
const COLS = [
  { k: 'total_sales', label: '売上', kind: 'yen' },
  { k: 'salesPct', label: '目標達成率', kind: 'pct', statusKey: 'status' },
  // 追加KGI (指名率 / 指名数). Sorting and the row alert stay on 売上 — 売上 is every stylist's KGI.
  { k: 'subKgiKey', label: '追加KGI', kind: 'subKgi' },
  { k: 'subKgiPct', label: '追加KGI達成率', kind: 'pct', statusKey: 'subKgiStatus' },
  { k: 'actionRate', label: '行動実施率', kind: 'pct', statusKey: 'actionStatus' },
  { k: 'unit_price', label: '客単価', kind: 'yen' },
  { k: 'customer_count', label: '客数', kind: 'num', unit: '名' },
  { k: 'nomination_rate', label: '指名率', kind: 'num', unit: '%' },
  { k: 'new_return_rate', label: '新規再来率', kind: 'num', unit: '%' },
  { k: 'salesTarget', label: '目標売上', kind: 'yen' },
  { k: 'goalDate', label: '目標時期', kind: 'ym' },
];

// good / mid / warn だけ色が付く（none・未設定は、色なし）
const STATUS_TONES = new Set(['good', 'mid', 'warn']);
const tone = (status) => (STATUS_TONES.has(status) ? status : 'none');

const yen = (v) => (v == null ? '—' : fmt('currency', v));
const ym = (d) => {
  const m = /^(\d{4})-(\d{2})/.exec(d || '');
  return m ? `${m[1]}年${Number(m[2])}月` : '—';
};

function cell(row, c) {
  const v = row[c.k];
  if (v == null || v === '') return '—';
  if (c.kind === 'yen') return yen(v);
  if (c.kind === 'ym') return ym(v);
  if (c.kind === 'subKgi') {
    const def = getFocusDef(v) || {};
    return <><b>{def.label || v}</b> {row.subKgiTarget != null ? fmt(def.unit, row.subKgiTarget) : '—'}</>;
  }
  if (c.kind === 'pct') return <span className={`rtb-pct rtb-pct--${tone(row[c.statusKey])}`}>{v}%</span>;
  return <>{Number(v).toLocaleString('ja-JP')}<span className="rtb-u">{c.unit}</span></>;
}

function focusLine(key, target, row) {
  if (!key) return null;
  const def = getFocusDef(key) || {};
  // Same reader Home uses: the registry knows which API field(s) hold this KPI's 実績.
  const actual = readKpiValue(KPI_BY_FOCUS_KEY[key], row);
  const f = (x) => (x == null ? '—' : fmt(def.unit, x));
  return (
    <span className="rtb-focus-i" key={key}>
      <b>{def.label || key}</b>
      {f(actual)} / {f(target)}
    </span>
  );
}

export default function StaffTable({ rows = [], loading }) {
  // Initial order = 目標達成率 ascending: the people who need follow-up come first.
  const [sort, setSort] = useState({ k: 'salesPct', dir: 'asc' });

  const sorted = useMemo(() => {
    const { k, dir } = sort;
    return [...rows].sort((a, b) => {
      const va = a[k];
      const vb = b[k];
      if (va == null && vb == null) return String(a.stylist).localeCompare(String(b.stylist), 'ja');
      if (va == null) return 1;
      if (vb == null) return -1;
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'ja');
      return dir === 'asc' ? c : -c;
    });
  }, [rows, sort]);

  const toggle = (k) => setSort((s) => ({ k, dir: s.k === k && s.dir === 'asc' ? 'desc' : 'asc' }));

  if (loading && rows.length === 0) return <div className="apv-empty">読み込み中…</div>;
  if (rows.length === 0) return <div className="apv-empty">この店舗のスタイリストはまだいません</div>;

  return (
    <>
      <div className="rst-head">
        <div className="rst-t">スタイリスト実績</div>
        <span className="rst-n">{rows.length}名</span>
      </div>
      <div className="rtb-hint">↓ 縦スクロールで全指標 · 見出しタップで並べ替え</div>
      <div className="rtb-hint rtb-hint--color">
        色：目標達成率・指名数は、月の経過に対するペースで判定（データが 7 日分に満たない間は色なし）。
        行動実施率・指名率は、値そのもので判定します。
      </div>
      <div className="rtb-wrap">
        <table className="rtb-table rtb-table--v">
          <thead>
            <tr>
              <th className="rtb-sticky rtb-corner rtb-tl">指標</th>
              {sorted.map((s) => {
                const alert = s.status === 'warn' || s.actionStatus === 'warn';
                return (
                  <th key={s.stylist} className={alert ? 'is-alert' : undefined}>
                    {alert && <span className="rtb-alert-dot" aria-label="要フォロー" />}
                    <span className="rtb-name">{s.stylist}</span>
                    {s.role && <span className="rtb-meta">{s.role}</span>}
                    {s.unregistered && <span className="rtb-meta">未登録</span>}
                    {s.approvalStatus === 'pending' && <span className="rtb-meta rtb-meta--wait">行動 確認中</span>}
                    {s.goalsApprovalStatus === 'pending' && <span className="rtb-meta rtb-meta--wait">目標 確認中</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {COLS.map((c) => (
              <tr key={c.k}>
                <th className="rtb-sticky rtb-tl" scope="row">
                  <button type="button" onClick={() => toggle(c.k)}>
                    {c.label}
                    <span className="rtb-ar">{sort.k === c.k ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
                  </button>
                </th>
                {sorted.map((s) => (
                  <td key={s.stylist}>{cell(s, c)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <th className="rtb-sticky rtb-tl" scope="row">注力指標</th>
              {sorted.map((s) => (
                <td key={s.stylist} className="rtb-tl">
                  <span className="rtb-focus">
                    {FOCUS_SLOTS.map((f) => focusLine(s[f.keyField], s[f.targetField], s))}
                    {!FOCUS_SLOTS.some((f) => s[f.keyField]) && '—'}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
